import React from 'react';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Crosshair,
  Eye,
  EyeOff,
  Layers3,
  Map as MapIcon,
  MapPin,
  Mountain,
  Navigation,
  Play,
  Route,
  Sparkles,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';

const localCopy = {
  fr: {
    alignment: 'Calage', terrains: 'Terrains', networks: 'Réseaux', obstacles: 'Obstacles', places: 'Lieux',
    layers: 'Calques', grid: 'Grille', pins: 'Pins', noPlaces: 'Aucun lieu notable sur cette carte.',
    placesHelp: 'Les pins restent les lieux de référence de la campagne : lore, quêtes et cartes liées sont conservés.',
    managePlaces: 'Gérer les pins dans Carte', prepared: 'Préparé', inProgress: 'En cours', complete: 'Terminé', draft: 'Brouillon',
    resume: 'Reprendre', prepare: 'Continuer la préparation', noJourney: 'Aucun voyage enregistré',
    noJourneyHelp: 'Créez un voyage depuis une carte préparée pour retrouver ici sa progression et son journal.',
    newJourney: 'Nouveau voyage', step: 'étape', steps: 'étapes', seed: 'Graine', map: 'Carte', universe: 'Univers',
  },
  en: {
    alignment: 'Alignment', terrains: 'Terrains', networks: 'Networks', obstacles: 'Obstacles', places: 'Places',
    layers: 'Layers', grid: 'Grid', pins: 'Pins', noPlaces: 'No notable place on this map.',
    placesHelp: 'Pins remain the campaign reference places: lore, quests, and linked maps are preserved.',
    managePlaces: 'Manage pins in Maps', prepared: 'Prepared', inProgress: 'In progress', complete: 'Complete', draft: 'Draft',
    resume: 'Resume', prepare: 'Continue planning', noJourney: 'No saved journey',
    noJourneyHelp: 'Create a journey from a prepared map to find its progress and log here.',
    newJourney: 'New journey', step: 'step', steps: 'steps', seed: 'Seed', map: 'Map', universe: 'Universe',
  },
};

export function TravelSetupRail({ language = 'fr', active, onChange }) {
  const text = localCopy[language] || localCopy.fr;
  const tools = [
    ['calibration', Crosshair, text.alignment],
    ['terrain', Mountain, text.terrains],
    ['network', Route, text.networks],
    ['condition', AlertTriangle, text.obstacles],
    ['places', MapPin, text.places],
  ];

  return (
    <nav className="flex w-[76px] shrink-0 flex-col border-r border-border bg-card/80 py-3" aria-label={language === 'fr' ? 'Outils de préparation' : 'Preparation tools'}>
      {tools.map(([id, Icon, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            'relative flex min-h-[70px] flex-col items-center justify-center gap-1.5 px-1 text-[10px] font-medium text-muted-foreground transition-colors',
            'hover:bg-secondary/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
            active === id && 'bg-primary/10 text-primary',
          )}
          aria-current={active === id ? 'page' : undefined}
        >
          {active === id && <span className="absolute inset-y-2 left-0 w-0.5 rounded-r bg-primary" />}
          <Icon className="h-5 w-5" />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

export function TravelLayersPopover({
  language = 'fr',
  gridVisible,
  onGridVisibleChange,
  terrainVisible,
  onTerrainVisibleChange,
  networkVisible,
  onNetworkVisibleChange,
  obstacleVisible,
  onObstacleVisibleChange,
  pinsVisible,
  onPinsVisibleChange,
  terrainOpacity = 34,
  onTerrainOpacityChange,
  align = 'end',
}) {
  const text = localCopy[language] || localCopy.fr;
  const rows = [
    ['grid', text.grid, gridVisible, onGridVisibleChange],
    ['terrain', text.terrains, terrainVisible, onTerrainVisibleChange],
    ['network', text.networks, networkVisible, onNetworkVisibleChange],
    ['obstacle', text.obstacles, obstacleVisible, onObstacleVisibleChange],
    ['pins', text.pins, pinsVisible, onPinsVisibleChange],
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="secondary" className="gap-2 border border-white/10 bg-black/75 text-white shadow-lg backdrop-blur hover:bg-black/90 hover:text-white">
          <Layers3 className="h-4 w-4" />{text.layers}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 border-white/10 bg-[#0d1620]/95 p-2 text-white shadow-2xl backdrop-blur-xl">
        <div className="mb-1 flex items-center gap-2 px-2 py-1.5">
          <Layers3 className="h-4 w-4" />
          <p className="font-cinzel text-sm font-semibold">{text.layers}</p>
        </div>
        {rows.map(([id, label, checked, onChange]) => (
          <div key={id} className="rounded-md px-2 py-2 hover:bg-white/5">
            <div className="flex items-center gap-2">
              {checked ? <Eye className="h-4 w-4 text-blue-400" /> : <EyeOff className="h-4 w-4 text-white/45" />}
              <label htmlFor={`travel-layer-${id}`} className="flex-1 text-sm">{label}</label>
              <Switch id={`travel-layer-${id}`} checked={Boolean(checked)} onCheckedChange={onChange} />
            </div>
            {id === 'terrain' && checked && onTerrainOpacityChange && (
              <div className="mt-2 flex items-center gap-3 pl-6">
                <Slider value={[terrainOpacity]} min={12} max={65} step={1} onValueChange={([value]) => onTerrainOpacityChange(value)} />
                <span className="w-8 text-right text-[10px] tabular-nums text-white/60">{terrainOpacity}%</span>
              </div>
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function TravelPlacesPanel({ language = 'fr', pins = [], pinsVisible, onPinsVisibleChange, onOpenMaps }) {
  const text = localCopy[language] || localCopy.fr;
  return (
    <div className="space-y-4 p-4">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-cinzel text-base font-bold">{text.places}</h2>
          <Badge variant="secondary">{pins.length}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{text.placesHelp}</p>
      </div>
      <div className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2">
        <span className="text-xs font-medium">{language === 'fr' ? 'Afficher le calque' : 'Show layer'}</span>
        <Switch checked={pinsVisible} onCheckedChange={onPinsVisibleChange} />
      </div>
      <div className="max-h-[46vh] space-y-1 overflow-auto pr-1">
        {pins.length ? pins.map((pin) => (
          <div key={pin.id} className="flex items-center gap-2 rounded-md border border-transparent px-2 py-2 text-sm hover:border-border hover:bg-muted/30">
            <MapPin className="h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0 flex-1 truncate">{pin.title}</span>
            {!pin.discovered && <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
        )) : <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">{text.noPlaces}</p>}
      </div>
      <Button type="button" variant="outline" className="w-full" onClick={onOpenMaps}>
        <MapIcon className="h-4 w-4" />{text.managePlaces}
      </Button>
    </div>
  );
}

const journeyStatus = (journey, text) => {
  if (journey.kind === 'draft') return { label: text.draft, Icon: Archive, className: 'text-amber-700 dark:text-amber-300' };
  if (journey.status === 'complete') return { label: text.complete, Icon: CheckCircle2, className: 'text-emerald-700 dark:text-emerald-300' };
  return { label: text.inProgress, Icon: Play, className: 'text-blue-700 dark:text-blue-300' };
};

export function TravelJourneyLibrary({ language = 'fr', journeys = [], onOpenJourney, onCreateJourney }) {
  const text = localCopy[language] || localCopy.fr;
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-5 py-6 md:px-8">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{language === 'fr' ? 'Registre de campagne' : 'Campaign register'}</p>
          <h2 className="mt-1 font-cinzel text-2xl font-bold">{language === 'fr' ? 'Voyages' : 'Journeys'}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{language === 'fr' ? 'Préparez, reprenez ou consultez les expéditions du groupe.' : 'Plan, resume, or review the party expeditions.'}</p>
        </div>
        <Button onClick={onCreateJourney}><Sparkles className="h-4 w-4" />{text.newJourney}</Button>
      </div>

      {journeys.length ? (
        <div className="overflow-hidden rounded-xl border bg-card/70">
          {journeys.map((journey, index) => {
            const status = journeyStatus(journey, text);
            const StatusIcon = status.Icon;
            const progress = journey.stepCount ? Math.min(100, Math.round(((journey.currentStepIndex || 0) + 1) / journey.stepCount * 100)) : 0;
            return (
              <button
                key={journey.id}
                type="button"
                onClick={() => onOpenJourney(journey)}
                className={cn(
                  'group grid w-full gap-4 px-4 py-4 text-left transition-colors hover:bg-secondary/45 md:grid-cols-[minmax(0,1.4fr)_minmax(150px,.7fr)_minmax(180px,.8fr)_auto] md:items-center',
                  index > 0 && 'border-t border-border/70',
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-muted/35 text-primary"><Navigation className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{journey.partyName || journey.mapName || (language === 'fr' ? 'Voyage sans titre' : 'Untitled journey')}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{journey.mapName} · {journey.packageName || journey.packageId}</p>
                  </div>
                </div>
                <div>
                  <Badge variant="outline" className={status.className}><StatusIcon className="mr-1 h-3 w-3" />{status.label}</Badge>
                  {journey.seed && <p className="mt-1.5 text-[10px] text-muted-foreground">{text.seed} · {journey.seed}</p>}
                </div>
                <div>
                  {journey.kind === 'run' ? (
                    <>
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} /></div>
                      <p className="mt-1.5 text-xs text-muted-foreground">{(journey.currentStepIndex || 0) + 1}/{journey.stepCount} {journey.stepCount > 1 ? text.steps : text.step}</p>
                    </>
                  ) : <p className="text-xs text-muted-foreground">{journey.startHexKey && journey.destinationHexKey ? `${journey.startHexKey} → ${journey.destinationHexKey}` : text.prepare}</p>}
                </div>
                <span className="text-xs font-semibold text-primary group-hover:underline">{journey.kind === 'run' ? text.resume : text.prepare}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed bg-card/30 p-10 text-center">
          <Navigation className="h-12 w-12 text-muted-foreground/40" />
          <h3 className="mt-4 font-cinzel text-lg font-semibold">{text.noJourney}</h3>
          <p className="mt-2 max-w-lg text-sm text-muted-foreground">{text.noJourneyHelp}</p>
          <Button className="mt-5" onClick={onCreateJourney}><Sparkles className="h-4 w-4" />{text.newJourney}</Button>
        </div>
      )}
    </div>
  );
}

